import { BookOpen, Globe, Building, Landmark, Users, Leaf, Brain, Beaker, TrendingUp, DollarSign, Calculator } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type SubjectColor = "history" | "science" | "math" | "social" | "economics";

type SubjectMetadataInternal = {
  iconComponent: LucideIcon;
  color: SubjectColor;
  difficulty?: "Easy" | "Medium" | "Hard";
  studyTime?: string;
  questionsEstimate?: number;
  description?: string;
};

export type SubjectMetadata = {
  iconComponent: LucideIcon;
  color: SubjectColor;
  difficulty?: "Easy" | "Medium" | "Hard";
  studyTime?: string;
  questionsEstimate?: number;
  description?: string;
};

const DEFAULT_METADATA_INTERNAL: SubjectMetadataInternal = {
  iconComponent: BookOpen,
  color: "history",
  description: "Build confidence with tailored practice sets and exam-style questions.",
};

const SUBJECT_METADATA_INTERNAL: Record<string, SubjectMetadataInternal> = {
  "world-history": {
    iconComponent: Globe,
    color: "history",
    difficulty: "Medium",
    studyTime: "4-6 hours/week",
    questionsEstimate: 4613,
    description: "Trace global developments from ancient civilizations to the modern era with thematic practice and timelines.",
  },
  "us-history": {
    iconComponent: Building,
    color: "history",
    difficulty: "Hard",
    studyTime: "5-7 hours/week",
    questionsEstimate: 6192,
    description:
      "Explore key events, court cases, and movements that shaped the United States with source-based practice.",
  },
  "european-history": {
    iconComponent: Landmark,
    color: "history",
    difficulty: "Hard",
    studyTime: "4-6 hours/week",
    questionsEstimate: 5333,
    description:
      "From Renaissance to the EU—master turning points, DBQs, and thematic connections across Europe.",
  },
  government: {
    iconComponent: Landmark,
    color: "social",
    difficulty: "Medium",
    studyTime: "3-4 hours/week",
    questionsEstimate: 3513,
    description:
      "Understand institutions, civil liberties, and policy-making through cases and comparative analysis.",
  },
  "us-government-and-politics": {
    iconComponent: Landmark,
    color: "social",
    difficulty: "Medium",
    studyTime: "3-4 hours/week",
    questionsEstimate: 3513,
    description:
      "Master foundational documents, Supreme Court cases, and political behavior with targeted practice.",
  },
  "united-states-history": {
    iconComponent: Building,
    color: "history",
    difficulty: "Hard",
    studyTime: "5-7 hours/week",
    questionsEstimate: 6192,
    description:
      "Chronological review of U.S. history with stimulus-based questions and periodization drills.",
  },
  "human-geography": {
    iconComponent: Users,
    color: "social",
    difficulty: "Easy",
    studyTime: "3-5 hours/week",
    questionsEstimate: 3993,
    description:
      "Analyze population, culture, and spatial patterns using models, maps, and applied case studies.",
  },
  "environmental-science": {
    iconComponent: Leaf,
    color: "science",
    difficulty: "Medium",
    studyTime: "4-6 hours/week",
    questionsEstimate: 5858,
    description:
      "Investigate ecosystems, energy, and sustainability with math-based items and scenario analysis.",
  },
  psychology: {
    iconComponent: Brain,
    color: "science",
    difficulty: "Easy",
    studyTime: "3-5 hours/week",
    questionsEstimate: 2275,
    description:
      "Learn cognition, development, and behavior with experiment-style questions and vocabulary checks.",
  },
  biology: {
    iconComponent: Beaker,
    color: "science",
    difficulty: "Hard",
    studyTime: "6-8 hours/week",
    questionsEstimate: 3922,
    description: "Build from cell processes to evolution with data-driven questions and lab-style prompts.",
  },
  microeconomics: {
    iconComponent: TrendingUp,
    color: "economics",
    difficulty: "Medium",
    studyTime: "3-5 hours/week",
    questionsEstimate: 2336,
    description: "Supply, demand, and market structures with graph interpretation and policy applications.",
  },
  macroeconomics: {
    iconComponent: DollarSign,
    color: "economics",
    difficulty: "Medium",
    studyTime: "3-5 hours/week",
    questionsEstimate: 2331,
    description: "Growth, inflation, and monetary/fiscal policy with model-based reasoning and scenario practice.",
  },
  "calculus-ab": {
    iconComponent: Calculator,
    color: "math",
    difficulty: "Hard",
    studyTime: "5-7 hours/week",
    questionsEstimate: 950,
    description: "Limits, derivatives, and integrals with conceptual explanations and AP-style FRQs.",
  },
  "calculus-bc": {
    iconComponent: Calculator,
    color: "math",
    difficulty: "Hard",
    studyTime: "6-8 hours/week",
    questionsEstimate: 1100,
    description: "Series, parametrics, and advanced integration—go beyond AB with full AP-style preparation.",
  },
};

export function getSubjectMetadata(slug: string): SubjectMetadata {
  const internal = SUBJECT_METADATA_INTERNAL[slug] ?? DEFAULT_METADATA_INTERNAL;
  return internal;
}
